import { clsx } from 'clsx';
import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';

type CheckboxProps = {
  label: ReactNode;
  error?: string;
  containerClassName?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'id'>;

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, error, containerClassName, className, checked, ...rest },
  ref,
) {
  const id = useId();
  const inputId = `cb-${id}`;
  const errorId = error ? `${inputId}-error` : undefined;

  return (
    <div className={clsx('flex flex-col gap-2', containerClassName)}>
      <label
        htmlFor={inputId}
        className="group flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-neutral-100"
      >
        <span className="relative flex-shrink-0">
          <input
            ref={ref}
            id={inputId}
            type="checkbox"
            checked={checked}
            className="peer sr-only"
            aria-describedby={errorId}
            aria-invalid={error ? true : undefined}
            {...rest}
          />
          <span
            className={clsx(
              'mt-0.5 inline-flex h-5 w-5 items-center justify-center rounded-sm border bg-night transition-colors duration-fast',
              'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-gold',
              checked ? 'border-gold bg-gold/15' : 'border-gold/40 group-hover:border-gold/70',
              error && !checked && 'border-error',
              className,
            )}
            aria-hidden="true"
          >
            {checked ? (
              <svg
                viewBox="0 0 20 20"
                className="h-3.5 w-3.5 text-gold"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 10.5l4 4 8-9" />
              </svg>
            ) : null}
          </span>
        </span>
        <span>{label}</span>
      </label>

      {error ? (
        <p
          id={errorId}
          role="alert"
          aria-live="polite"
          className="ml-8 text-xs text-error"
        >
          {error}
        </p>
      ) : null}
    </div>
  );
});
